import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  useAppType,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetAllUserVerificationFarmTasks,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldsByFarmId,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useNavigate,
  useTranslation,
  useUpdateFarmSetting,
} from "utils";

import { ALL_FIELDS_ROUTE } from "consts";

import { PRACTICE_SLUG, PracticeSlug } from "models";

import { Flex, Icon, MainPage, Tabs, Typography, VideoOverlay, Message as message } from "components";

import { getCropsWithMissingHistoricalPracticeDataByYear } from "pages/app/all-field-view/utils/AllFieldsUtils";
import { DataTableViewOnboardingModal } from "pages/app/historical-practice-data-table-view/components/DataTableViewOnboardingModal";
import { GuidanceBanner } from "pages/app/historical-practice-data-table-view/components/GuidanceBanner";
import { MainLoadingSkeleton } from "pages/app/historical-practice-data-table-view/components/LoadingSkeletons";
import { MissingDataTable } from "pages/app/historical-practice-data-table-view/components/MissingDataTable";
import { type HistoricalPracticeFiltersState, PracticeFilters } from "pages/app/historical-practice-data-table-view/components/PracticeFilters";
import { SidePanel } from "pages/app/historical-practice-data-table-view/components/SidePanel";
import { useTableBodyHeight } from "pages/app/historical-practice-data-table-view/hooks/useTableBodyHeight";
import { isYearComplete } from "pages/app/historical-practice-data-table-view/utils/HistoricalDataUtils";

// Full Vimeo URL with privacy hash - required for unlisted/private videos.
export const HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL = "https://player.vimeo.com/video/1157306195?h=435e502125";

export const HistoricalPracticeDataTableView = () => {
  const { t } = useTranslation("historicalPracticeDataTableView");
  const { smallDesktop } = useAppType();
  const navigate = useNavigate();
  const { currentFarm, isLoading: isFarmsLoading } = useGetFarms();
  const { data: carbonProgramOutcomes, isLoading: isCarbonProgramOutcomesLoading } = useGetAllCarbonProgramOutcomes(
    currentFarm?.id,
    currentFarm?.carbonProgram
  );
  const { data: fields, isLoading: isFieldsLoading } = useGetFieldsByFarmId({ farmId: currentFarm?.id ?? "" });
  const { data: allFieldsPractices, isLoading: isAllFieldPracticesLoading } = useGetAllFieldPracticesByFarm(currentFarm);
  const {
    data: { activeFarmReportingYear },
    isLoading: isFarmReportingYearLoading,
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isLoading: isSeedingPracticesLoading } = useGetPracticeSeedings({ enabled: false });
  const { isLoading: isTillagePracticesLoading } = useGetPracticeTillages({ enabled: false });
  const { isLoading: isOrganicFertilizerPracticesLoading } = useGetPracticeOrganicFertilizers({ enabled: false });
  const { data: verificationFarmTasks, isLoading: isGetVerificationTasksLoading } = useGetAllUserVerificationFarmTasks(currentFarm?.id);
  const { mutate: mutateFarmSetting } = useUpdateFarmSetting();

  const isPracticesDataLoading = isSeedingPracticesLoading || isTillagePracticesLoading || isOrganicFertilizerPracticesLoading;
  const isTableDataLoading =
    isFarmsLoading ||
    isFieldsLoading ||
    isCarbonProgramOutcomesLoading ||
    isAllFieldPracticesLoading ||
    isFarmReportingYearLoading ||
    isPracticesDataLoading ||
    isGetVerificationTasksLoading;

  const [selectedYear, setSelectedYear] = useState<number>();
  const defaultFilters: HistoricalPracticeFiltersState = {
    appliedCropIds: [],
    previouslyAppliedCropIds: [],
    missingGrowingPeriod: false,
    missingOrganicFertilizers: false,
    missingSeedings: false,
    missingTillages: false,
  };
  const [filters, setFilters] = useState<HistoricalPracticeFiltersState>(defaultFilters);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState<boolean | null>(null);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [isFileUploadDrawerOpen, setIsFileUploadDrawerOpen] = useState(false);

  const hasAnyFiltersApplied =
    filters.appliedCropIds.length > 0 ||
    filters.previouslyAppliedCropIds.length > 0 ||
    filters.missingGrowingPeriod ||
    filters.missingOrganicFertilizers ||
    filters.missingSeedings ||
    filters.missingTillages;

  const handleClearAll = () => setFilters(defaultFilters);

  const saveAndExit = useCallback(() => {
    message({ content: t("sidePanel.messages.saveSuccess"), icon: "tick" });
    navigate(ALL_FIELDS_ROUTE, { state: { openTasksDrawer: true } });
  }, [navigate, t]);

  const cropsWithMissingHistoricalPracticeDataByYear = getCropsWithMissingHistoricalPracticeDataByYear(
    fields ?? [],
    carbonProgramOutcomes ?? {},
    currentFarm?.carbonProgram ?? null,
    allFieldsPractices,
    activeFarmReportingYear
  );

  const requiredPracticeSlugs = Object.values(cropsWithMissingHistoricalPracticeDataByYear).reduce((acc, yearEntries) => {
    if (acc.includes(PRACTICE_SLUG.TILLAGES) && acc.includes(PRACTICE_SLUG.ORGANIC_FERTILIZERS) && acc.includes(PRACTICE_SLUG.SEEDINGS)) return acc;

    if (!acc.includes(PRACTICE_SLUG.TILLAGES)) {
      const isTillageRequired = yearEntries.some((entry) => entry.assignedPractices.tillages !== null);
      if (isTillageRequired) acc.push(PRACTICE_SLUG.TILLAGES);
    }
    if (!acc.includes(PRACTICE_SLUG.ORGANIC_FERTILIZERS)) {
      const isOrganicFertilizerRequired = yearEntries.some((entry) => entry.assignedPractices.organicFertilizers !== null);
      if (isOrganicFertilizerRequired) acc.push(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
    }
    if (!acc.includes(PRACTICE_SLUG.SEEDINGS)) {
      const isSeedingRequired = yearEntries.some((entry) => entry.assignedPractices.seedings !== null);
      if (isSeedingRequired) acc.push(PRACTICE_SLUG.SEEDINGS);
    }
    return acc;
  }, [] as PracticeSlug[]);

  const isOnboardingModalDismissed = currentFarm?.farmSettings?.find((setting) => setting.item === "dataGapOnboardingModalDismissed")?.value === "true";
  const shouldShowOnboardingModal = isOnboardingModalOpen !== null ? isOnboardingModalOpen : !isOnboardingModalDismissed;
  const shouldShowLoadingSkeleton = isTableDataLoading || shouldShowOnboardingModal;

  const handleOnboardingComplete = () => {
    mutateFarmSetting({ item: "dataGapOnboardingModalDismissed", value: "true" });
  };

  const { availableYears, completedYears, completedYearsSet } = useMemo(() => {
    const years = Object.keys(cropsWithMissingHistoricalPracticeDataByYear)
      .map((year) => parseInt(year))
      .sort();
    const completed = years.filter((y) => isYearComplete(y, cropsWithMissingHistoricalPracticeDataByYear));
    return {
      availableYears: years,
      completedYears: completed,
      completedYearsSet: new Set(completed),
    };
  }, [cropsWithMissingHistoricalPracticeDataByYear]);

  const fileUploadTaskProgress = useMemo(() => {
    if (!verificationFarmTasks) return { total: 0, completed: 0 };

    return {
      total: verificationFarmTasks.length,
      completed: verificationFarmTasks.filter((task) => !!task.completedAt).length,
    };
  }, [verificationFarmTasks]);

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // re-run when table content is ready (selectedYear + data for that year) so header height is measured after the table has rendered
  const tableDataLength = selectedYear ? cropsWithMissingHistoricalPracticeDataByYear[selectedYear]?.length ?? -1 : -1;
  const { tableWrapperRef, tableBodyHeight } = useTableBodyHeight({
    selectedYear,
    tableDataLength,
    isWrapperHidden: shouldShowLoadingSkeleton,
  });

  const navigationCrumbs = {
    crumbs: [
      { name: currentFarm?.name || t("modules:navigation.overview.farm"), truncate: true, replacedByFarmSwitch: true },
      { name: t("fieldTimeline:header.fieldManagementCrumb"), link: ALL_FIELDS_ROUTE },
    ],
    additionalData: <></>,
  };

  return (
    <MainPage
      fullWidth
      pageName="historical-practice-data-table-view-page"
      navigationCrumbs={navigationCrumbs}
      showMobileAlert
      share={{ title: t("pageTitle") }}
    >
      <Flex
        className="historical-practice-data-table-view container"
        data-testid="historical-practice-data-table-view-page-container"
        justify="space-between"
        gap="1rem"
      >
        {shouldShowLoadingSkeleton ? (
          <MainLoadingSkeleton />
        ) : (
          <Flex className="historical-practice-data-table-view-main-container" vertical gap="1rem">
            <Flex
              className={`historical-practice-data-table-view-year-selector-row ${smallDesktop ? "small-desktop" : ""}`}
              justify={smallDesktop ? "flex-start" : "space-between"}
              align={smallDesktop ? "flex-start" : "center"}
              gap={smallDesktop ? "1rem" : 0}
              vertical={smallDesktop}
            >
              <Typography variant="h5">{t("yearSelectorTitle")}</Typography>
              <Tabs
                items={availableYears.map((year) => ({
                  label: (
                    <span className="tab-label">
                      {
                        <Typography variant="h6" className="tab-label-text">
                          {year.toString()}
                        </Typography>
                      }
                      {!completedYearsSet.has(year) && <span className="dot red" />}
                      {completedYearsSet.has(year) && <Icon className="check green" iconName="tabler/check" strokeWidth="4" size="xs" />}
                    </span>
                  ),
                  key: year.toString(),
                }))}
                className="historical-practice-data-table-view-year-selector"
                activeKey={selectedYear?.toString()}
                onChange={(key) => setSelectedYear(parseInt(key))}
              />
            </Flex>
            <Flex align="center" className="historical-practice-data-table-view-filters">
              <PracticeFilters
                cropsWithMissingHistoricalPracticeDataByYear={cropsWithMissingHistoricalPracticeDataByYear}
                selectedYear={selectedYear}
                filters={filters}
                setFilters={setFilters}
                onClearAll={handleClearAll}
              />
            </Flex>
            {availableYears.length > 0 && (
              <Flex align="center" className="historical-practice-data-table-view-banner">
                <GuidanceBanner
                  availableYears={availableYears}
                  completedYears={completedYears}
                  selectedYear={selectedYear}
                  hasOpenFileUploadTasks={fileUploadTaskProgress.total > 0 && fileUploadTaskProgress.completed < fileUploadTaskProgress.total}
                  onSelectYear={(year: number) => setSelectedYear(year)}
                  onShowVideo={() => setShowVideoOverlay(true)}
                  onNavigateToFileUpload={() => setIsFileUploadDrawerOpen(true)}
                  onSaveAndExit={saveAndExit}
                />
              </Flex>
            )}
            <div className="historical-practice-data-table-view-table" ref={tableWrapperRef}>
              <MissingDataTable
                allFieldPractices={allFieldsPractices}
                cropsWithMissingHistoricalPracticeDataByYear={cropsWithMissingHistoricalPracticeDataByYear}
                fields={fields}
                filters={filters}
                hasFiltersActive={hasAnyFiltersApplied}
                requiredPracticeSlugs={requiredPracticeSlugs}
                selectedYear={selectedYear}
                tableBodyHeight={tableBodyHeight}
                onClearFilters={handleClearAll}
              />
            </div>
          </Flex>
        )}
        <SidePanel
          cropsWithMissingHistoricalPracticeDataByYear={cropsWithMissingHistoricalPracticeDataByYear}
          availableYears={availableYears}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          isLoading={shouldShowLoadingSkeleton}
          fileUploadTaskProgress={fileUploadTaskProgress}
          isFileUploadDrawerOpen={isFileUploadDrawerOpen}
          onFileUploadDrawerOpenChange={setIsFileUploadDrawerOpen}
          onSaveAndExit={saveAndExit}
        />
      </Flex>
      {!isTableDataLoading && (
        <DataTableViewOnboardingModal isOpen={shouldShowOnboardingModal} setIsOpen={setIsOnboardingModalOpen} onComplete={handleOnboardingComplete} />
      )}
      <VideoOverlay visible={showVideoOverlay} vimeoId={HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL} onClose={() => setShowVideoOverlay(false)} />
    </MainPage>
  );
};
